// Client-side image resize via Pica. Mirrors 38recipes' resizeAndScaleImage
// (lib/imageResize.js) — same algorithm, same unsharp parameters, output
// is a single webp blob scaled to the target width preserving aspect
// ratio. Browser-only — call from a "use client" component.
import Pica from "pica";

// Disable Web Workers — Turbopack's runtime isn't available inside workers.
// Keep "cib" (createImageBitmap) — it's independent of workers and critical
// for efficient large-image handling on iOS Safari.
const pica = Pica({ features: ["js", "wasm", "cib"] });

const RECIPE_HERO_WIDTH = 1200;
const DIRECTION_IMAGE_WIDTH = 800;
const PROFILE_PHOTO_WIDTH = 512;

// iOS Safari caps decoded image memory — images above this pixel count can
// cause silent drawImage failures or OOM. Pre-scale to stay within limits.
const MAX_SOURCE_PIXELS = 16_777_216; // 4096×4096

// img.decode() throws "Load error" on iOS Safari for large camera photos.
// Using onload allows the browser to decode lazily when drawing to a smaller canvas.
async function loadImage(file: File): Promise<HTMLImageElement> {
  const img = new Image();
  const url = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("image_load_failed"));
      img.src = url;
    });
  } catch {
    URL.revokeObjectURL(url);
    throw new Error("image_load_failed");
  }
  URL.revokeObjectURL(url);
  return img;
}

// Pre-scale oversized images to stay within iOS canvas memory limits.
// Returns a canvas at reduced dimensions if the source exceeds MAX_SOURCE_PIXELS.
function preScale(
  img: HTMLImageElement,
  targetWidth: number
): HTMLCanvasElement | HTMLImageElement {
  const pixels = img.naturalWidth * img.naturalHeight;
  if (pixels <= MAX_SOURCE_PIXELS) return img;

  const scale = Math.min(Math.sqrt(MAX_SOURCE_PIXELS / pixels), targetWidth / img.naturalWidth);
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

export interface ResizedImage {
  blob: Blob;
  aspectRatio: number;
}

export async function resizeRecipeHero(file: File): Promise<ResizedImage> {
  const img = await loadImage(file);

  const aspectRatio = img.naturalWidth / img.naturalHeight;
  const targetWidth = Math.min(RECIPE_HERO_WIDTH, img.naturalWidth);
  const targetHeight = Math.round(targetWidth / aspectRatio);

  const source = preScale(img, targetWidth);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  await pica.resize(source, canvas, {
    unsharpAmount: 110,
    unsharpRadius: 0.6,
    unsharpThreshold: 1,
  });

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.9)
  );
  if (!blob) throw new Error("canvas_toBlob_returned_null");

  return { blob, aspectRatio };
}

export async function resizeDirectionImage(file: File): Promise<ResizedImage> {
  const img = await loadImage(file);

  const aspectRatio = img.naturalWidth / img.naturalHeight;
  const targetWidth = Math.min(DIRECTION_IMAGE_WIDTH, img.naturalWidth);
  const targetHeight = Math.round(targetWidth / aspectRatio);

  const source = preScale(img, targetWidth);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  await pica.resize(source, canvas, {
    unsharpAmount: 110,
    unsharpRadius: 0.6,
    unsharpThreshold: 1,
  });

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.85)
  );
  if (!blob) throw new Error("canvas_toBlob_returned_null");

  return { blob, aspectRatio };
}

export async function resizeProfilePhoto(file: File): Promise<ResizedImage> {
  const img = await loadImage(file);

  const aspectRatio = img.naturalWidth / img.naturalHeight;
  const targetWidth = Math.min(PROFILE_PHOTO_WIDTH, img.naturalWidth);
  const targetHeight = Math.round(targetWidth / aspectRatio);

  const source = preScale(img, targetWidth);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  await pica.resize(source, canvas, {
    unsharpAmount: 80,
    unsharpRadius: 0.5,
    unsharpThreshold: 1,
  });

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.95)
  );
  if (!blob) throw new Error("canvas_toBlob_returned_null");

  return { blob, aspectRatio };
}
